import { useState, useEffect, useCallback } from 'react';
import {
  Package, Check, Plus, ChevronDown, ChevronUp, X, Loader2,
  RefreshCw, ShoppingCart, AlertTriangle, Link2, ArrowRight, Trash2, Cable
} from 'lucide-react';
import { Button, Card, Spinner } from '../common';
import { toast } from 'sonner';
import api from '../../utils/api';

// ==================== OCI IMPORT PANEL ====================

const OciImportPanel = ({ isOpen, onClose, onProductsChanged }) => {
  const [callbacks, setCallbacks] = useState([]);
  const [loading, setLoading] = useState(false);
  const [expandedCallback, setExpandedCallback] = useState(null);
  const [processingItem, setProcessingItem] = useState(null);
  const [allProducts, setAllProducts] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);

  // Daten laden
  const loadCallbacks = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/oci/results');
      setCallbacks(res.data);
      // Automatisch neuesten Callback expandieren wenn noch keiner offen
      if (res.data.length > 0 && expandedCallback === null) {
        const newest = res.data.find(c => c.status === 'received');
        if (newest) setExpandedCallback(newest.id);
      }
    } catch (err) {
      toast.error('Fehler beim Laden der OCI-Daten');
    } finally {
      setLoading(false);
    }
  }, []);

  // Alle Produkte laden (für manuelle Zuordnung)
  const loadProducts = useCallback(async () => {
    try {
      const res = await api.get('/products');
      setAllProducts(res.data);
    } catch (err) {
      console.error('Products load error:', err);
    }
  }, []);

  useEffect(() => {
    if (isOpen) {
      loadCallbacks();
      loadProducts();
    }
  }, [isOpen, loadCallbacks, loadProducts]);

  // Produkt-Suche für manuelle Zuordnung
  const handleSearch = (query) => {
    setSearchQuery(query);
    if (!query || query.length < 2) {
      setSearchResults([]);
      return;
    }
    const q = query.toLowerCase();
    const results = allProducts.filter(p =>
      p.name?.toLowerCase().includes(q) ||
      p.article_number?.toLowerCase().includes(q) ||
      p.manufacturer_sku?.toLowerCase().includes(q)
    ).slice(0, 8);
    setSearchResults(results);
  };

  // Artikel zu bestehendem Produkt hinzufügen
  const addToProduct = async (productId, ociItem, supplier = 'Zajadacz') => {
    setProcessingItem(ociItem.index);
    try {
      await api.post('/oci/process/add-source', {
        productId,
        supplier,
        ociItem,
      });
      toast.success(`Preis zu bestehendem Produkt hinzugefügt`);
      onProductsChanged?.();
      // Item aus lokaler Ansicht als verarbeitet markieren
      markItemProcessed(ociItem);
    } catch (err) {
      toast.error(err.response?.data?.error || 'Fehler beim Hinzufügen');
    } finally {
      setProcessingItem(null);
      setSearchQuery('');
      setSearchResults([]);
    }
  };

  // Neues Produkt aus OCI-Daten anlegen
  const createProduct = async (ociItem, supplier = 'Zajadacz') => {
    setProcessingItem(ociItem.index);
    try {
      const res = await api.post('/oci/process/create-product', {
        supplier,
        ociItem,
      });
      toast.success(`Neues Produkt angelegt: ${res.data.product?.name}`);
      onProductsChanged?.();
      loadProducts(); // Refresh product list for future matches
      markItemProcessed(ociItem);
    } catch (err) {
      toast.error(err.response?.data?.error || 'Fehler beim Anlegen');
    } finally {
      setProcessingItem(null);
    }
  };

  // Item lokal als verarbeitet markieren
  const markItemProcessed = (ociItem) => {
    setCallbacks(prev => prev.map(cb => ({
      ...cb,
      items: cb.items.map(item =>
        item.index === ociItem.index && item.vendorMat === ociItem.vendorMat
          ? { ...item, _processed: true }
          : item
      ),
    })));
  };

  // Callback löschen
  const deleteCallback = async (id) => {
    try {
      await api.delete(`/oci/results/${id}`);
      setCallbacks(prev => prev.filter(c => c.id !== id));
      toast.success('Eintrag gelöscht');
    } catch (err) {
      toast.error('Fehler beim Löschen');
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />

      {/* Panel (Slide-in von rechts) */}
      <div className="absolute right-0 top-0 bottom-0 w-full max-w-2xl bg-white shadow-2xl flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b bg-gray-50">
          <div className="flex items-center gap-3">
            <ShoppingCart className="w-5 h-5 text-blue-600" />
            <div>
              <h2 className="text-lg font-semibold text-gray-900">OCI Import</h2>
              <p className="text-sm text-gray-500">
                {callbacks.length} Übertragung{callbacks.length !== 1 ? 'en' : ''}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="ghost" size="sm" onClick={loadCallbacks} title="Aktualisieren">
              <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            </Button>
            <Button variant="ghost" size="sm" onClick={onClose}>
              <X className="w-5 h-5" />
            </Button>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-4 space-y-3">
          {loading && callbacks.length === 0 ? (
            <div className="text-center py-12">
              <Loader2 className="w-8 h-8 text-blue-500 animate-spin mx-auto mb-3" />
              <p className="text-gray-500">Lade OCI-Daten...</p>
            </div>
          ) : callbacks.length === 0 ? (
            <div className="text-center py-12">
              <ShoppingCart className="w-12 h-12 text-gray-300 mx-auto mb-4" />
              <p className="text-gray-500 font-medium">Keine OCI-Übertragungen</p>
              <p className="text-gray-400 text-sm mt-1">
                Öffne den Zajadacz Shop und sende Artikel per "An OCI senden"
              </p>
            </div>
          ) : (
            callbacks.map(callback => (
              <CallbackCard
                key={callback.id}
                callback={callback}
                isExpanded={expandedCallback === callback.id}
                onToggle={() => setExpandedCallback(
                  expandedCallback === callback.id ? null : callback.id
                )}
                onDelete={() => deleteCallback(callback.id)}
                onAddToProduct={addToProduct}
                onCreateProduct={createProduct}
                processingItem={processingItem}
                searchQuery={searchQuery}
                searchResults={searchResults}
                onSearch={handleSearch}
              />
            ))
          )}
        </div>
      </div>
    </div>
  );
};

// ==================== CALLBACK CARD ====================

const CallbackCard = ({
  callback, isExpanded, onToggle, onDelete,
  onAddToProduct, onCreateProduct, processingItem,
  searchQuery, searchResults, onSearch,
}) => {
  const date = new Date(callback.createdAt);
  const timeStr = date.toLocaleString('de-DE', {
    day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit',
  });
  const unprocessed = callback.items?.filter(i => !i._processed).length || 0;
  const isBulk = callback.callbackType === 'bulk_update';

  return (
    <div className="border rounded-xl overflow-hidden bg-white">
      {/* Header */}
      <button
        onClick={onToggle}
        className="w-full flex items-center gap-3 px-4 py-3 hover:bg-gray-50 transition-colors"
      >
        <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${
          isBulk ? 'bg-purple-100' : unprocessed > 0 ? 'bg-blue-100' : 'bg-green-100'
        }`}>
          {isBulk ? (
            <RefreshCw className="w-4 h-4 text-purple-600" />
          ) : unprocessed > 0 ? (
            <Package className="w-4 h-4 text-blue-600" />
          ) : (
            <Check className="w-4 h-4 text-green-600" />
          )}
        </div>
        <div className="flex-1 text-left">
          <div className="flex items-center gap-2">
            <span className="font-medium text-sm text-gray-900">
              {callback.supplier} — {callback.itemCount} Artikel
            </span>
            {isBulk && (
              <span className="text-xs bg-purple-100 text-purple-700 px-2 py-0.5 rounded-full">
                Bulk-Update
              </span>
            )}
          </div>
          <span className="text-xs text-gray-500">{timeStr}</span>
        </div>
        {unprocessed > 0 && !isBulk && (
          <span className="text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded-full">
            {unprocessed} offen
          </span>
        )}
        <Button
          variant="ghost"
          size="sm"
          onClick={(e) => { e.stopPropagation(); onDelete(); }}
          className="text-gray-400 hover:text-red-500 p-1"
        >
          <Trash2 className="w-3.5 h-3.5" />
        </Button>
        {isExpanded ? (
          <ChevronUp className="w-4 h-4 text-gray-400" />
        ) : (
          <ChevronDown className="w-4 h-4 text-gray-400" />
        )}
      </button>

      {/* Expanded Content */}
      {isExpanded && callback.items && (
        <div className="border-t divide-y">
          {callback.items.map((item, idx) => (
            <OciItemRow
              key={`${item.vendorMat}-${idx}`}
              item={item}
              supplier={callback.supplier}
              onAddToProduct={onAddToProduct}
              onCreateProduct={onCreateProduct}
              isProcessing={processingItem === item.index}
              searchQuery={searchQuery}
              searchResults={searchResults}
              onSearch={onSearch}
            />
          ))}
        </div>
      )}
    </div>
  );
};

// ==================== OCI ITEM ROW ====================

const OciItemRow = ({
  item, supplier, onAddToProduct, onCreateProduct, isProcessing,
  searchQuery, searchResults, onSearch,
}) => {
  const [showManualSearch, setShowManualSearch] = useState(false);
  const [activeSearch, setActiveSearch] = useState(false);

  if (item._processed) {
    return (
      <div className="px-4 py-2 bg-green-50/50 flex items-center gap-3">
        <Check className="w-4 h-4 text-green-500 flex-shrink-0" />
        <span className="text-sm text-green-700 truncate">{item.description}</span>
        <span className="text-xs text-green-500 ml-auto">verarbeitet</span>
      </div>
    );
  }

  const bestMatch = item.matches?.[0];
  const hasHighConfidence = bestMatch?.confidence >= 0.9;
  const priceFormatted = item.price
    ? Number(item.price).toLocaleString('de-DE', { minimumFractionDigits: 4, maximumFractionDigits: 4 })
    : '—';

  return (
    <div className="px-4 py-3">
      {/* Artikelinfo */}
      <div className="flex items-start gap-3 mb-2">
        <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 mt-0.5 ${
          item.isCable ? 'bg-orange-100' : 'bg-gray-100'
        }`}>
          {item.isCable ? (
            <Cable className="w-4 h-4 text-orange-600" />
          ) : (
            <Package className="w-4 h-4 text-gray-500" />
          )}
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium text-gray-900 truncate" title={item.description}>
            {item.description?.replace(/\*+/g, '').replace(/\s{2,}/g, ' ').trim() || 'Unbenannt'}
          </p>
          <div className="flex items-center gap-3 mt-1 text-xs text-gray-500">
            <span>Art: {item.vendorMat || '—'}</span>
            {item.manufacturerSku && (
              <span className="bg-gray-100 px-1.5 py-0.5 rounded">Hst: {item.manufacturerSku}</span>
            )}
            {item.cableInfo && (
              <span className="bg-orange-50 text-orange-700 px-1.5 py-0.5 rounded">
                {item.cableInfo.searchKey}
              </span>
            )}
          </div>
          <div className="flex items-center gap-3 mt-1 text-xs text-gray-500">
            <span>{item.quantity} {item.unit}</span>
            <span className="font-mono text-gray-700">{priceFormatted} €/{item.unit || 'Einh.'}</span>
            {item.priceUnit > 1 && (
              <span className="text-gray-400">(Roh: {item.priceRaw}€ / {item.priceUnit})</span>
            )}
          </div>
        </div>
      </div>

      {/* Match-Bereich */}
      {isProcessing ? (
        <div className="flex items-center gap-2 py-2 text-sm text-blue-600">
          <Loader2 className="w-4 h-4 animate-spin" />
          <span>Wird verarbeitet...</span>
        </div>
      ) : (
        <div className="mt-2">
          {/* Auto-Match Vorschlag */}
          {bestMatch && (
            <div className={`rounded-lg border p-2.5 mb-2 ${
              hasHighConfidence
                ? 'bg-green-50 border-green-200'
                : 'bg-amber-50 border-amber-200'
            }`}>
              <div className="flex items-center gap-2">
                {hasHighConfidence ? (
                  <Check className="w-4 h-4 text-green-600 flex-shrink-0" />
                ) : (
                  <AlertTriangle className="w-4 h-4 text-amber-500 flex-shrink-0" />
                )}
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-gray-800 truncate">
                    {bestMatch.productName}
                  </p>
                  <p className="text-xs text-gray-500">
                    {bestMatch.matchType === 'cable_exact' && 'Kabel-Match (exakt)'}
                    {bestMatch.matchType === 'cable_normalized' && 'Kabel-Match (normalisiert)'}
                    {bestMatch.matchType === 'manufacturer_sku_exact' && 'Hersteller-Art.Nr. (exakt)'}
                    {bestMatch.matchType === 'source_sku_match' && 'Bereits bei anderem Shop'}
                    {bestMatch.matchType === 'vendor_article_match' && 'Lieferanten-Art.Nr.'}
                    {bestMatch.matchType === 'name_contains_sku' && 'Name enthält Art.Nr.'}
                    {' — '}{Math.round(bestMatch.confidence * 100)}%
                  </p>
                </div>
                <Button
                  variant="primary"
                  size="sm"
                  className="flex-shrink-0"
                  onClick={() => onAddToProduct(bestMatch.productId, item, supplier)}
                >
                  <ArrowRight className="w-3.5 h-3.5 mr-1" />
                  Zuordnen
                </Button>
              </div>

              {/* Weitere Matches */}
              {item.matches?.length > 1 && (
                <div className="mt-2 pt-2 border-t border-green-200/60">
                  <p className="text-xs text-gray-500 mb-1">
                    Weitere Treffer:
                  </p>
                  {item.matches.slice(1, 3).map((m, i) => (
                    <div key={i} className="flex items-center justify-between py-1">
                      <span className="text-xs text-gray-600 truncate mr-2">{m.productName}</span>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="text-xs flex-shrink-0 py-0.5 px-2"
                        onClick={() => onAddToProduct(m.productId, item, supplier)}
                      >
                        Zuordnen
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Action Buttons */}
          <div className="flex gap-2">
            {/* Manuelle Suche Toggle */}
            <Button
              variant="outline"
              size="sm"
              className="flex-1 text-xs"
              onClick={() => {
                setShowManualSearch(!showManualSearch);
                setActiveSearch(!showManualSearch);
              }}
            >
              <Link2 className="w-3.5 h-3.5 mr-1" />
              Manuell zuordnen
            </Button>
            {/* Neues Produkt */}
            <Button
              variant="outline"
              size="sm"
              className="flex-1 text-xs"
              onClick={() => onCreateProduct(item, supplier)}
            >
              <Plus className="w-3.5 h-3.5 mr-1" />
              Neues Produkt
            </Button>
          </div>

          {/* Manuelle Suche */}
          {showManualSearch && activeSearch && (
            <div className="mt-2 border rounded-lg p-2 bg-gray-50">
              <input
                type="text"
                placeholder="Produkt suchen (Name, Art.Nr.)..."
                className="w-full px-3 py-1.5 text-sm border rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                value={searchQuery}
                onChange={(e) => onSearch(e.target.value)}
                autoFocus
              />
              {searchResults.length > 0 && (
                <div className="mt-2 max-h-40 overflow-y-auto divide-y">
                  {searchResults.map(product => (
                    <div
                      key={product.id}
                      className="flex items-center justify-between py-1.5 hover:bg-white rounded px-2 cursor-pointer"
                      onClick={() => onAddToProduct(product.id, item, supplier)}
                    >
                      <div className="min-w-0 flex-1">
                        <p className="text-sm text-gray-800 truncate">{product.name}</p>
                        <p className="text-xs text-gray-500">
                          {product.article_number || ''} {product.category ? `· ${product.category}` : ''}
                        </p>
                      </div>
                      <ArrowRight className="w-3.5 h-3.5 text-gray-400 flex-shrink-0 ml-2" />
                    </div>
                  ))}
                </div>
              )}
              {searchQuery.length >= 2 && searchResults.length === 0 && (
                <p className="text-xs text-gray-400 mt-2 text-center">
                  Keine Produkte gefunden
                </p>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default OciImportPanel;
